// Hashing — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/arrays.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer on the approach(es) worth animating.

const hashingProblems = [
  {
    id: 'two-sum',
    title: 'Two Sum',
    difficulty: 'Easy',
    leetcode: { number: 1, url: 'https://leetcode.com/problems/two-sum/' },
    statement:
      'Given an array of integers nums and an integer target, return the indices of the two numbers ' +
      'that add up to target. Assume exactly one solution exists, and you may not use the same element twice.',
    example: {
      input: 'nums = [3, 2, 4], target = 6',
      output: '[1, 2]',
      note: 'nums[1] + nums[2] = 2 + 4 = 6.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Pair)',
        why:
          "The direct reading of the problem: try every pair of indices and check if they sum to target. " +
          "Correct, but every pair gets checked even when the answer could have been found on the very first one.",
        steps: [
          'Fix an index i, for every position in the array.',
          'Try every later index j > i as the second number.',
          'If nums[i] + nums[j] equals target, return both indices immediately.',
          'Every pair is checked in the worst case — O(n²).',
        ],
        visual: {
          arrays: [{ label: 'nums (target = 6)', values: [3, 2, 4] }],
          varNames: [],
          steps: [
            { highlights: { nums: { primary: [0], secondary: [1] } }, note: '3 + 2 = 5. Not 6.' },
            { highlights: { nums: { primary: [0], secondary: [2] } }, note: '3 + 4 = 7. Not 6.' },
            { highlights: { nums: { primary: [1], secondary: [2] } }, note: '2 + 4 = 6. Match! Return [1, 2].' },
          ],
        },
        code:
`public int[] twoSumBruteForce(int[] nums, int target) {
    for (int i = 0; i < nums.length; i++) {
        for (int j = i + 1; j < nums.length; j++) {
            if (nums[i] + nums[j] == target) {
                return new int[] { i, j };
            }
        }
    }
    return new int[] {};
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
        whenToUse:
          "Say this out loud first to confirm you understand the pairing requirement, then optimize — never " +
          "leave O(n²) as your final answer for a problem this fundamental; it signals you don't know the pattern.",
      },
      {
        name: 'Approach 2 — Optimized: Complement Lookup with a HashMap',
        why:
          "Instead of checking every other element against the current one, keep a map of everything already " +
          "seen. For the current number, its complement (target − current) either already exists in the map or " +
          "it doesn't — checking that is O(1), so the whole problem collapses to one pass.",
        steps: [
          'Keep a map from value → index for everything seen so far.',
          'For each number, compute complement = target − current.',
          'If the complement is already in the map, return its index and the current index.',
          'Otherwise, insert the current number and its index, and continue.',
        ],
        visual: {
          arrays: [{ label: 'nums (target = 6)', values: [3, 2, 4] }],
          varNames: [],
          steps: [
            { highlights: { nums: [0] }, note: '3: complement = 3. Not in map. Insert 3 → 0. map = {3:0}.' },
            { highlights: { nums: [1] }, note: '2: complement = 4. Not in map. Insert 2 → 1. map = {3:0, 2:1}.' },
            { highlights: { nums: [2] }, note: '4: complement = 2. Found at index 1! Return [1, 2].' },
          ],
        },
        code:
`public int[] twoSum(int[] nums, int target) {
    Map<Integer, Integer> indexOf = new HashMap<>();
    for (int i = 0; i < nums.length; i++) {
        int complement = target - nums[i];
        if (indexOf.containsKey(complement)) {
            return new int[] { indexOf.get(complement), i };
        }
        indexOf.put(nums[i], i);
    }
    return new int[] {};
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
        whenToUse:
          "This is the canonical hashing pattern — reach for a value→index map whenever you need to find a " +
          "complement or a pair in one pass instead of comparing every element against every other one.",
      },
    ],
  },
  {
    id: 'contains-duplicate',
    title: 'Contains Duplicate',
    difficulty: 'Easy',
    leetcode: { number: 217, url: 'https://leetcode.com/problems/contains-duplicate/' },
    statement:
      'Given an integer array nums, return true if any value appears at least twice, and false if every ' +
      'element is distinct.',
    example: {
      input: 'nums = [1, 2, 3, 1]',
      output: 'true',
      note: 'The value 1 appears twice.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Check Every Pair)',
        why: "Compare every element against every later element, looking for any match at all.",
        steps: [
          'For each element, compare it against every later element.',
          'If any two match, a duplicate exists — return true immediately.',
          'If no match is ever found across every pair, return false.',
          'Every pair is checked in the worst case — O(n²).',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [1, 2, 3, 1] }],
          varNames: [],
          steps: [
            { highlights: { nums: { primary: [0], secondary: [1] } }, note: '1 vs 2: no match.' },
            { highlights: { nums: { primary: [0], secondary: [2] } }, note: '1 vs 3: no match.' },
            { highlights: { nums: { primary: [0], secondary: [3] } }, note: '1 vs 1: match! Duplicate found — return true.' },
          ],
        },
        code:
`public boolean containsDuplicateBruteForce(int[] nums) {
    for (int i = 0; i < nums.length; i++) {
        for (int j = i + 1; j < nums.length; j++) {
            if (nums[i] == nums[j]) return true;
        }
    }
    return false;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
        whenToUse: "Only useful to confirm the problem statement out loud — never a real final answer once n grows.",
      },
      {
        name: 'Approach 2 — Sort First, Then Scan Neighbors',
        why:
          "Sorting brings equal values right next to each other, so a single adjacent-pair scan afterward is " +
          "enough — no need to compare every element to every other one.",
        steps: [
          'Sort a copy of the array.',
          'Walk through adjacent pairs only.',
          'If two neighbors are equal, a duplicate exists.',
          'If the scan finishes with no match, all elements are distinct.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [1, 2, 3, 1] }],
          varNames: [],
          steps: [
            { highlights: {}, note: 'Original: [1, 2, 3, 1].' },
            { snapshots: { nums: [1, 1, 2, 3] }, note: 'After sorting: [1, 1, 2, 3] — the two 1s are now adjacent.' },
            { highlights: { nums: [0, 1] }, note: 'Compare adjacent: sorted[0]=1 equals sorted[1]=1 → duplicate found!' },
          ],
        },
        code:
`public boolean containsDuplicateSorting(int[] nums) {
    int[] sorted = Arrays.copyOf(nums, nums.length);
    Arrays.sort(sorted);
    for (int i = 1; i < sorted.length; i++) {
        if (sorted[i] == sorted[i - 1]) return true;
    }
    return false;
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
        whenToUse:
          "Choose this when you want O(1) extra space and can sort in place (dropping the defensive copy) — " +
          "otherwise the HashSet version below is simpler to write and just as fast on average.",
      },
      {
        name: 'Approach 3 — Optimized: HashSet',
        why:
          "Set.add() already tells you, via its return value, whether the element was present before you added " +
          "it — so the insert and the duplicate-check are literally the same line of code.",
        steps: [
          'Keep a HashSet of numbers seen so far.',
          'For each number, try to add it to the set.',
          'If add() returns false, that number was already present — duplicate found.',
          'If every add() succeeds, all elements are distinct.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [1, 2, 3, 1] }],
          varNames: [],
          steps: [
            { highlights: { nums: [0] }, note: 'Add 1 to the set. seen = {1}.' },
            { highlights: { nums: [1] }, note: 'Add 2 to the set. seen = {1, 2}.' },
            { highlights: { nums: [2] }, note: 'Add 3 to the set. seen = {1, 2, 3}.' },
            { highlights: { nums: [3] }, note: 'Try to add 1 again — add() returns false. Duplicate found! Return true.' },
          ],
        },
        code:
`public boolean containsDuplicate(int[] nums) {
    Set<Integer> seen = new HashSet<>();
    for (int num : nums) {
        if (!seen.add(num)) {
            return true;
        }
    }
    return false;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
        interviewNote: "The trade-off worth naming out loud: HashSet is O(n) time but O(n) space; sorting in place is O(n log n) time but O(1) extra space. Either is a correct final answer if you can explain the trade-off.",
        whenToUse:
          "This is the default answer for this problem — O(n) time, and add()'s boolean return value does double " +
          "duty as both the insert and the check, in a single line.",
      },
    ],
  },
  {
    id: 'group-anagrams',
    title: 'Group Anagrams',
    difficulty: 'Medium',
    leetcode: { number: 49, url: 'https://leetcode.com/problems/group-anagrams/' },
    statement:
      'Given an array of strings strs, group the anagrams together. Anagrams are words made of exactly the ' +
      'same letters, rearranged. Return the groups in any order.',
    example: {
      input: 'strs = ["eat", "tea", "tan", "ate", "nat", "bat"]',
      output: '[["eat","tea","ate"], ["tan","nat"], ["bat"]]',
      note: '"eat", "tea", and "ate" all contain exactly the letters a, e, t.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Compare Every Pair of Words)',
        why:
          "For each word not yet grouped, compare it against every other ungrouped word to check if they're " +
          "anagrams of each other (by sorting both and comparing), collecting matches into a group as you go.",
        steps: [
          'For each word not yet grouped, start a new group containing just it.',
          'Compare every other ungrouped word against it by sorting both and checking equality.',
          'Add any match found to the same group, and mark it as grouped.',
          'Every word gets compared against every other word — O(n² · k log k), where k is word length.',
        ],
        code:
`public List<List<String>> groupAnagramsBruteForce(String[] strs) {
    List<List<String>> groups = new ArrayList<>();
    boolean[] used = new boolean[strs.length];

    for (int i = 0; i < strs.length; i++) {
        if (used[i]) continue;
        List<String> group = new ArrayList<>();
        group.add(strs[i]);
        used[i] = true;
        for (int j = i + 1; j < strs.length; j++) {
            if (!used[j] && isAnagram(strs[i], strs[j])) {
                group.add(strs[j]);
                used[j] = true;
            }
        }
        groups.add(group);
    }
    return groups;
}

private boolean isAnagram(String a, String b) {
    char[] aChars = a.toCharArray(), bChars = b.toCharArray();
    Arrays.sort(aChars);
    Arrays.sort(bChars);
    return Arrays.equals(aChars, bChars);
}`,
        complexity: { time: 'O(n² · k log k)', space: 'O(n · k)' },
        whenToUse:
          "Only useful to confirm you understand what defines an anagram before optimizing — pairwise " +
          "comparison doesn't scale past a handful of words.",
      },
      {
        name: 'Approach 2 — Optimized: Group by Canonical Signature',
        why:
          "Give every word a canonical signature — its characters sorted alphabetically. Anagrams always " +
          "produce the identical signature, so instead of comparing words to each other, bucket them by that " +
          "signature in a HashMap in a single pass.",
        steps: [
          'For each word, sort its characters to build a canonical signature.',
          'Use that signature as a HashMap key.',
          'Append the original word to the list stored under that signature.',
          'One pass over the words, one sort per word — O(n · k log k), with no pairwise comparisons at all.',
        ],
        visual: {
          arrays: [
            { label: 'strs', values: ['eat', 'tea', 'tan', 'ate', 'nat', 'bat'] },
            { label: 'signature', values: [null, null, null, null, null, null] },
          ],
          varNames: [],
          steps: [
            { highlights: { strs: [0], signature: { secondary: [0] } }, snapshots: { signature: ['aet', null, null, null, null, null] }, note: '"eat" sorted → "aet". Grouped under key "aet".' },
            { highlights: { strs: [1], signature: { secondary: [1] } }, snapshots: { signature: ['aet', 'aet', null, null, null, null] }, note: '"tea" sorted → "aet" — same key as "eat"! Joins that group.' },
            { highlights: { strs: [2], signature: { secondary: [2] } }, snapshots: { signature: ['aet', 'aet', 'ant', null, null, null] }, note: '"tan" sorted → "ant". A new group.' },
            { highlights: { strs: [3], signature: { secondary: [3] } }, snapshots: { signature: ['aet', 'aet', 'ant', 'aet', null, null] }, note: '"ate" sorted → "aet" — joins the "eat"/"tea" group.' },
            { highlights: { strs: [4], signature: { secondary: [4] } }, snapshots: { signature: ['aet', 'aet', 'ant', 'aet', 'ant', null] }, note: '"nat" sorted → "ant" — joins the "tan" group.' },
            { highlights: { strs: [5], signature: { secondary: [5] } }, snapshots: { signature: ['aet', 'aet', 'ant', 'aet', 'ant', 'abt'] }, note: '"bat" sorted → "abt" — its own group. Final: [eat,tea,ate], [tan,nat], [bat].' },
          ],
        },
        code:
`public List<List<String>> groupAnagrams(String[] strs) {
    Map<String, List<String>> groups = new HashMap<>();
    for (String word : strs) {
        char[] chars = word.toCharArray();
        Arrays.sort(chars);
        String signature = new String(chars);
        groups.computeIfAbsent(signature, k -> new ArrayList<>()).add(word);
    }
    return new ArrayList<>(groups.values());
}`,
        complexity: { time: 'O(n · k log k)', space: 'O(n · k)' },
        interviewNote: "Try the \"Group Anagrams\" mode in this pattern's interactive playground (in the Explanation modal) to run this exact algorithm on your own list of words.",
        whenToUse:
          "This signature-bucketing idea generalizes well beyond anagrams — any time two items should be " +
          "considered \"the same\" by some derived property, compute a canonical key and group by it in a HashMap.",
      },
    ],
  },
  {
    id: 'top-k-frequent-elements',
    title: 'Top K Frequent Elements',
    difficulty: 'Medium',
    leetcode: { number: 347, url: 'https://leetcode.com/problems/top-k-frequent-elements/' },
    statement:
      'Given an integer array nums and an integer k, return the k most frequent elements. The answer can be ' +
      'returned in any order.',
    example: {
      input: 'nums = [1, 1, 1, 2, 2, 3], k = 2',
      output: '[1, 2]',
      note: '1 appears 3 times, 2 appears 2 times — the two most frequent values.',
    },
    approaches: [
      {
        name: 'Approach 1 — Count, Then Sort by Frequency',
        why:
          "Count every element's frequency first, then sort all distinct elements by that frequency in " +
          "descending order and take the first k. Simple, but sorting is more power than this actually needs.",
        steps: [
          'Build a frequency map for every element, one pass.',
          'Convert the map\'s distinct keys into a list.',
          'Sort that list by frequency, descending.',
          'Take the first k elements from the sorted list.',
        ],
        visual: {
          arrays: [
            { label: 'nums (k = 2)', values: [1, 1, 1, 2, 2, 3] },
            { label: 'freq[1,2,3]', values: [0, 0, 0] },
          ],
          varNames: [],
          steps: [
            { highlights: { nums: [0] }, snapshots: { 'freq[1,2,3]': [1, 0, 0] }, note: 'nums[0]=1 → freq[1] = 1.' },
            { highlights: { nums: [1] }, snapshots: { 'freq[1,2,3]': [2, 0, 0] }, note: 'nums[1]=1 → freq[1] = 2.' },
            { highlights: { nums: [2] }, snapshots: { 'freq[1,2,3]': [3, 0, 0] }, note: 'nums[2]=1 → freq[1] = 3.' },
            { highlights: { nums: [3] }, snapshots: { 'freq[1,2,3]': [3, 1, 0] }, note: 'nums[3]=2 → freq[2] = 1.' },
            { highlights: { nums: [4] }, snapshots: { 'freq[1,2,3]': [3, 2, 0] }, note: 'nums[4]=2 → freq[2] = 2.' },
            { highlights: { nums: [5] }, snapshots: { 'freq[1,2,3]': [3, 2, 1] }, note: 'nums[5]=3 → freq[3] = 1. Counting done: {1:3, 2:2, 3:1}.' },
            { note: 'Sort keys by frequency descending: [1 (3), 2 (2), 3 (1)]. Take the first k=2: [1, 2].' },
          ],
        },
        code:
`public int[] topKFrequentSorting(int[] nums, int k) {
    Map<Integer, Integer> freq = new HashMap<>();
    for (int num : nums) {
        freq.put(num, freq.getOrDefault(num, 0) + 1);
    }

    List<Integer> keys = new ArrayList<>(freq.keySet());
    keys.sort((a, b) -> freq.get(b) - freq.get(a)); // descending by frequency

    int[] result = new int[k];
    for (int i = 0; i < k; i++) {
        result[i] = keys.get(i);
    }
    return result;
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
        whenToUse:
          "Reach for this when clarity matters more than optimality, or k is close to n anyway — sorting is " +
          "simple to write correctly under interview pressure.",
      },
      {
        name: 'Approach 2 — Optimized: Bucket Sort by Frequency',
        why:
          "A frequency can never exceed n (the array length), so instead of a comparison-based sort, create " +
          "n+1 \"buckets\" indexed directly by frequency and drop each distinct element into its bucket. Reading " +
          "buckets from the highest frequency down gives the answer without ever comparing two elements.",
        steps: [
          'Build the frequency map, same as before.',
          'Create buckets[0..n], where buckets[f] will hold every element with frequency exactly f.',
          'Place each distinct element into buckets[its frequency].',
          'Walk the buckets from the highest frequency down to 1, collecting elements until you have k.',
        ],
        visual: {
          arrays: [{ label: 'buckets (index = frequency)', values: [null, null, null, null, null, null, null] }],
          varNames: [],
          steps: [
            { note: 'Frequencies already computed: {1: 3, 2: 2, 3: 1}.' },
            { highlights: { 'buckets (index = frequency)': [3] }, snapshots: { 'buckets (index = frequency)': [null, null, null, '[1]', null, null, null] }, note: 'Element 1 has frequency 3 → placed in buckets[3].' },
            { highlights: { 'buckets (index = frequency)': [2] }, snapshots: { 'buckets (index = frequency)': [null, null, '[2]', '[1]', null, null, null] }, note: 'Element 2 has frequency 2 → placed in buckets[2].' },
            { highlights: { 'buckets (index = frequency)': [1] }, snapshots: { 'buckets (index = frequency)': [null, '[3]', '[2]', '[1]', null, null, null] }, note: 'Element 3 has frequency 1 → placed in buckets[1].' },
            { highlights: { 'buckets (index = frequency)': [6, 5, 4] }, note: 'Scan from the highest bucket (6) downward: buckets 6, 5, 4 are empty — skip.' },
            { highlights: { 'buckets (index = frequency)': [3] }, note: 'buckets[3] = [1] → take 1. Collected so far: [1].' },
            { highlights: { 'buckets (index = frequency)': [2] }, note: 'buckets[2] = [2] → take 2. k=2 reached. Final answer: [1, 2] — O(n), no comparison sort.' },
          ],
        },
        code:
`public int[] topKFrequent(int[] nums, int k) {
    Map<Integer, Integer> freq = new HashMap<>();
    for (int num : nums) {
        freq.put(num, freq.getOrDefault(num, 0) + 1);
    }

    List<Integer>[] buckets = new List[nums.length + 1];
    for (int num : freq.keySet()) {
        int f = freq.get(num);
        if (buckets[f] == null) buckets[f] = new ArrayList<>();
        buckets[f].add(num);
    }

    int[] result = new int[k];
    int idx = 0;
    for (int f = buckets.length - 1; f >= 0 && idx < k; f--) {
        if (buckets[f] == null) continue;
        for (int num : buckets[f]) {
            if (idx == k) break;
            result[idx++] = num;
        }
    }
    return result;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
        interviewNote: "A heap of size k (O(n log k)) is the other standard answer here — worth mentioning as an alternative even if bucket sort is your primary answer.",
        whenToUse:
          "Bucket sort by frequency is the go-to O(n) answer whenever you need to rank items by a bounded " +
          "count — frequency can never exceed n, so you never need more than n+1 buckets.",
      },
    ],
  },
];

export default hashingProblems;
